import React, { useState } from 'react';
import { sendRingOfSixMessage } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Brain, Heart, Hand, Footprints, Eye, Sparkles, Send, Loader } from 'lucide-react';

const RingOfSixChat = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const perspectiveIcons = {
    'MIND': <Brain size={20} />,
    'HEART': <Heart size={20} />,
    'HANDS': <Hand size={20} />,
    'LEGS': <Footprints size={20} />,
    'EYE': <Eye size={20} />,
    'AGENT': <Sparkles size={20} />
  };

  const perspectiveColors = {
    'MIND': '#00ffff',
    'HEART': '#ff0080',
    'HANDS': '#00ff88',
    'LEGS': '#ffa500',
    'EYE': '#8b00ff',
    'AGENT': '#ffff00'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    try {
      const response = await sendRingOfSixMessage(message);
      setResult(response);
    } catch (error) {
      console.error('Ring of Six error:', error);
      alert('Error getting response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 100%)',
      padding: '2rem',
      color: '#fff'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            background: 'linear-gradient(135deg, #00ffff, #00ff88)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem'
          }}>
            Ring of Six Perspectives
          </h1>
          <p style={{ color: '#888', fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
            Explore questions through 6 different lenses - like thoughts bouncing around in the human mind.
            Agent Zero verifies truth and synthesizes wisdom from all perspectives.
          </p>
        </div>

        {/* Input Form */}
        <Card style={{ 
          padding: '2rem', 
          background: 'rgba(10,10,15,0.8)',
          border: '1px solid rgba(0,255,255,0.2)',
          marginBottom: '2rem'
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask a question to explore from all perspectives..."
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'rgba(0,0,0,0.5)',
                  border: '1px solid rgba(0,255,255,0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
              <Button 
                type="submit" 
                disabled={loading || !message.trim()}
                style={{
                  background: loading ? '#333' : 'linear-gradient(135deg, #00ffff, #00ff88)',
                  color: '#000',
                  padding: '1rem 2rem',
                  fontWeight: 'bold'
                }}
              >
                {loading ? <Loader className="animate-spin" size={20} /> : <Send size={20} />}
                {loading ? 'Consulting...' : 'Ask'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Results */}
        {result && (
          <div>
            {/* Agent Zero Verdict */}
            <Card style={{
              padding: '2rem',
              background: result.verdict === 'PASS' 
                ? 'rgba(0,255,136,0.1)' 
                : result.verdict === 'FLAG' 
                  ? 'rgba(255,165,0,0.1)' 
                  : 'rgba(255,0,0,0.1)',
              border: `2px solid ${
                result.verdict === 'PASS' 
                  ? '#00ff88' 
                  : result.verdict === 'FLAG' 
                    ? '#ffa500' 
                    : '#ff0000'
              }`,
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#000'
                }}>
                  0
                </div>
                <div>
                  <h2 style={{ fontSize: '1.5rem', color: '#00ffff', marginBottom: '0.25rem' }}>
                    Agent Zero Truth Buffer
                  </h2>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <span style={{ 
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: result.verdict === 'PASS' ? '#00ff88' : result.verdict === 'FLAG' ? '#ffa500' : '#ff0000'
                    }}>
                      Verdict: {result.verdict}
                    </span>
                    <span style={{ color: '#888' }}>
                      Confidence: {(result.confidence_score * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
              <p style={{ color: '#ccc', fontSize: '0.9rem', fontStyle: 'italic' }}>
                {result.agent_zero_notes}
              </p>
            </Card>

            {/* All 6 Perspectives */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              {result.perspectives?.map((perspective) => (
                <Card 
                  key={perspective.perspective || perspective.name}
                  style={{
                    padding: '1.5rem',
                    background: 'rgba(10,10,15,0.8)',
                    border: `2px solid ${perspectiveColors[perspective.perspective]}`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${perspectiveColors[perspective.perspective]}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ color: perspectiveColors[perspective.perspective] }}>
                      {perspectiveIcons[perspective.perspective]}
                    </div>
                    <div>
                      <h3 style={{ 
                        fontSize: '1.2rem', 
                        color: perspectiveColors[perspective.perspective],
                        marginBottom: '0.25rem'
                      }}>
                        {perspective.perspective}
                      </h3>
                      <p style={{ fontSize: '0.85rem', color: '#888' }}>
                        {perspective.focus}
                      </p>
                    </div>
                  </div>
                  <p style={{ color: '#ccc', lineHeight: '1.6' }}>
                    {perspective.response}
                  </p>
                </Card>
              ))}
            </div>

            {/* Synthesized Response */}
            <Card style={{
              padding: '2rem',
              background: 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(0,255,136,0.1))',
              border: '2px solid #00ffff'
            }}>
              <h2 style={{ 
                fontSize: '1.5rem', 
                color: '#00ffff', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Sparkles size={24} />
                Synthesized Truth-Verified Answer
              </h2>
              <div style={{ 
                color: '#fff', 
                lineHeight: '1.8',
                fontSize: '1.1rem',
                whiteSpace: 'pre-wrap'
              }}>
                {result.response}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default RingOfSixChat;
